import { z } from 'zod';
import { HexColour, IsoDateTime, Locale, TimeZone, Uuid, patchOf } from './common.js';
import { TenantRestriction } from './core-p4.js';
import { BadgeMedia } from './badges.js';
import { GroupKind, GroupsGroupSettings } from './groups.js';
import { SitesSiteSettings } from './sites.js';
/**
 * Kiosk devices: pairing, device config, health, alerts, printers, monitoring and remote actions
 * (core device identity + module `kiosk-devices`; docs/modules/kiosk-devices.md, ADR 0008). Owners: C1b (pairing and
 * device auth), S7 (management, config, health, alerts, printers), S9 (history, uptime, remote actions).
 */
export const DeviceKind = z.enum(['kiosk', 'display', 'companion']);
export const DevicePlatform = z.enum(['android', 'ios']);
// ---------- Pairing (core) ----------
/** POST /kiosk-devices/pairing-codes (sites.manage_devices). */
export const PairingCodeCreate = z.object({
    siteId: Uuid,
    name: z.string().trim().min(1).max(60),
    kind: DeviceKind.default('kiosk'),
});
export const PairingCode = z.object({
    /** 9 digits (7 for older codes), shown once; only its hash is stored. */
    code: z.string().regex(/^(\d{7}|\d{9})$/),
    expiresAt: IsoDateTime,
});
export const DeviceInfo = z.object({
    platform: DevicePlatform,
    osVersion: z.string().max(40),
    /** Native app version (app.json version). */
    appVersion: z.string().max(40),
    /** Screens version (over-the-air bundle), may equal appVersion. */
    bundleVersion: z.string().max(40),
    model: z.string().max(80),
});
/** POST /devices/pair (public, rate-limited). */
export const PairRequest = z.object({ code: z.string().regex(/^(\d{7}|\d{9})$/), device: DeviceInfo });
export const PairResponse = z.object({
    deviceId: Uuid,
    /** Long-lived bearer token: `Authorization: Device <token>`. Shown once; store in secure storage. */
    deviceToken: z.string(),
    tenantName: z.string(),
    siteId: Uuid,
    siteName: z.string(),
    /** Shared reception (ADR 0034): absent or 'tenant' for an ordinary kiosk. */
    kind: z.literal('tenant').optional(),
});
/**
 * Shared reception (ADR 0034, task C-SR2): a building pairing code pairs the tablet as a shared kiosk. The token is the
 * building token (`Authorization: SharedKiosk <token>`); company links come later from POST /shared-reception/device/links.
 * Older kiosk bundles fail to parse this answer, so they can never enter shared mode by accident.
 */
export const SharedPairResponse = z.object({
    kind: z.literal('building'),
    sharedDeviceId: Uuid,
    buildingToken: z.string(),
    buildingId: Uuid,
    buildingName: z.string(),
});
/** POST /devices/pair answers one of these (tenant codes are tried first, then building codes). */
export const PairResult = z.union([PairResponse, SharedPairResponse]);
// ---------- Health ----------
export const PrinterState = z.enum(['ready', 'no_media', 'cover_open', 'media_mismatch', 'busy', 'error', 'unreachable', 'unknown']);
export const PrinterStatus = z.object({
    printerId: Uuid.nullable(),
    state: PrinterState,
    /** Media the printer says is loaded, when it reports it. */
    media: BadgeMedia.nullable(),
    /** Labels left, when the printer reports it (many Brother rolls don't). */
    labelsLeft: z.number().int().nullable(),
    /** Raw error bits as hex, for support. */
    errorCode: z.string().max(40).nullable(),
    checkedAt: IsoDateTime,
});
/** POST /kiosk-devices/device/health (device access), every healthReportSeconds. */
export const HealthReport = z.object({
    deviceTime: IsoDateTime,
    batteryPercent: z.number().int().min(0).max(100).nullable(),
    charging: z.boolean().nullable(),
    network: z.enum(['wifi', 'ethernet', 'cellular', 'none', 'unknown']),
    /** 0-100, Wi-Fi only. */
    wifiStrength: z.number().int().min(0).max(100).nullable(),
    freeStorageMb: z.number().int().min(0).nullable(),
    appVersion: z.string().max(40),
    bundleVersion: z.string().max(40),
    osVersion: z.string().max(40),
    printer: PrinterStatus.nullable(),
    /** Events still waiting in the offline queue. */
    queueLength: z.number().int().min(0),
    lastSignInAt: IsoDateTime.nullable(),
    /** Phase 3: events the server refused that stay on the tablet as dead letters. */
    deadLetters: z.number().int().min(0).optional(),
    /** Phase 3: when the oldest unsent event was queued (null = nothing waiting). */
    oldestQueuedAt: IsoDateTime.nullable().optional(),
    /** Phase 3: NFC reader state (`none` = no reader, `off` = switched off, `ready`). */
    nfc: z.enum(['none', 'off', 'ready']).optional(),
    /** Phase 3: size of the cached induction media, in MB. */
    mediaCacheMb: z.number().int().min(0).optional(),
});
export const HealthReportResponse = z.object({
    serverTime: IsoDateTime,
    /** When it differs from the kiosk's copy, the kiosk refetches its config. */
    configEtag: z.string(),
    nextReportSeconds: z.number().int(),
});
export const DeviceAlertKind = z.enum(['offline', 'battery_low', 'not_charging', 'printer_fault', 'labels_low', 'app_outdated', 'queue_stuck']);
export const DeviceAlert = z.object({
    id: Uuid,
    deviceId: Uuid,
    kind: DeviceAlertKind,
    openedAt: IsoDateTime,
    clearedAt: IsoDateTime.nullable(),
    /** e.g. { batteryPercent: 12 }. No personal data. */
    details: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
    emailedAt: IsoDateTime.nullable(),
    clearEmailedAt: IsoDateTime.nullable(),
});
// ---------- Devices (portal) ----------
export const Device = z.object({
    id: Uuid,
    kind: DeviceKind,
    siteId: Uuid,
    name: z.string(),
    status: z.enum(['active', 'revoked']),
    platform: DevicePlatform.nullable(),
    model: z.string().nullable(),
    osVersion: z.string().nullable(),
    appVersion: z.string().nullable(),
    bundleVersion: z.string().nullable(),
    /** Platform setting latestAppVersion/latestBundleVersion is newer. */
    updateAvailable: z.boolean(),
    pairedAt: IsoDateTime,
    lastSeenAt: IsoDateTime.nullable(),
    online: z.boolean(),
    printerId: Uuid.nullable(),
    /** Latest health report, null before the first. */
    health: HealthReport.omit({ deviceTime: true }).extend({ reportedAt: IsoDateTime }).nullable(),
    alertsEnabled: z.boolean(),
    openAlerts: z.array(DeviceAlertKind),
    /** Shared reception (ADR 0034, C-SR2): set when this device is this company's link on a shared kiosk. Optional so older mocks type-check. */
    shared: z.object({ buildingId: Uuid, buildingName: z.string(), approval: z.enum(['pending', 'approved', 'denied']).optional() }).nullable().optional(),
});
export const DeviceUpdate = z.object({
    name: z.string().trim().min(1).max(60).optional(),
    siteId: Uuid.optional(),
    printerId: Uuid.nullable().optional(),
    alertsEnabled: z.boolean().optional(),
});
export const DeviceListQuery = z.object({ siteId: Uuid.optional(), status: z.enum(['active', 'revoked', 'all']).default('active') });
// ---------- Printers ----------
export const PrinterModel = z.enum(['brother_ql_820nwb', 'brother_ql_other']);
export const Printer = z.object({
    id: Uuid,
    siteId: Uuid,
    name: z.string(),
    model: PrinterModel,
    /** IP address or host name on the site network; the kiosk connects to it directly. */
    host: z.string(),
    port: z.number().int(),
    media: BadgeMedia,
    status: z.enum(['active', 'archived']),
    lastStatus: PrinterStatus.nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const PrinterCreate = z.object({
    siteId: Uuid,
    name: z.string().trim().min(1).max(60),
    model: PrinterModel.default('brother_ql_820nwb'),
    host: z.string().trim().min(1).max(253).regex(/^[A-Za-z0-9.-]+$/),
    port: z.number().int().min(1).max(65535).default(9100),
    media: BadgeMedia,
});
export const PrinterUpdate = patchOf(PrinterCreate.omit({ siteId: true })).extend({ status: z.enum(['active', 'archived']).optional() });
// ---------- Monitoring (S9) ----------
export const DeviceHistoryQuery = z.object({ days: z.coerce.number().int().min(1).max(30).default(7) });
export const DeviceHistory = z.object({
    deviceId: Uuid,
    from: IsoDateTime,
    to: IsoDateTime,
    /** Online minutes / minutes in the range since pairing, 0-100, one decimal. */
    uptimePercent: z.number(),
    hours: z.array(z.object({
        hour: IsoDateTime,
        onlineMinutes: z.number().int(),
        batteryMin: z.number().int().nullable(),
        batteryAvg: z.number().int().nullable(),
        wifiAvg: z.number().int().nullable(),
        chargingMinutes: z.number().int(),
        printerFaultMinutes: z.number().int(),
        signIns: z.number().int(),
    })),
});
export const RemoteActionKind = z.enum(['reload_config', 'restart_app', 'test_print', 'show_message', 'unpair', 'nfc_enrol', 'evacuation_mode']);
/** POST /kiosk-devices/:id/actions (devices.remote_actions). */
export const RemoteActionRequest = z.discriminatedUnion('action', [
    z.object({ action: z.literal('reload_config') }),
    z.object({ action: z.literal('restart_app') }),
    z.object({ action: z.literal('test_print') }),
    z.object({ action: z.literal('show_message'), message: z.string().trim().min(1).max(200), seconds: z.number().int().min(5).max(3600).default(60) }),
    z.object({ action: z.literal('unpair') }),
]);
export const DeviceCommand = z.object({
    id: Uuid,
    action: RemoteActionKind,
    params: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
    createdAt: IsoDateTime,
    /** Commands not delivered by then are dropped. */
    expiresAt: IsoDateTime,
    ackedAt: IsoDateTime.nullable(),
    result: z.enum(['ok', 'failed']).nullable(),
});
/** POST /kiosk-devices/device/commands/:id/ack */
export const DeviceCommandAck = z.object({ result: z.enum(['ok', 'failed']), errorCode: z.string().max(60).nullable().default(null) });
// ---------- Kiosk config (GET /kiosk-devices/device/config, device access) ----------
/** One step of the sign-in or sign-out pipeline (ADR 0008 "Step pipeline"). config is typed by the owning module. */
export const KioskStep = z.object({
    key: z.string(),
    module: z.string(),
    order: z.number().int(),
    config: z.unknown(),
});
export const KioskGroup = z.object({
    id: Uuid,
    name: z.string(),
    kind: GroupKind,
    sortOrder: z.number().int(),
    settings: GroupsGroupSettings,
    /** ADR 0008 "PINs on the kiosk". */
    kioskPinHash: z.string().nullable(),
    signInSteps: z.array(KioskStep),
    signOutSteps: z.array(KioskStep),
});
export const KioskConfig = z.object({
    /** Changes whenever anything below changes. Sent as ETag; the kiosk sends If-None-Match. */
    etag: z.string(),
    generatedAt: IsoDateTime,
    tenant: z.object({
        name: z.string(),
        primaryColour: HexColour,
        accentColour: HexColour,
        logo: z.object({ fileId: Uuid, url: z.string() }).nullable(),
    }),
    site: z.object({
        id: Uuid,
        name: z.string(),
        timeZone: TimeZone,
        language: Locale,
        /** Per-site secret (base64url) for checking PIN values offline; given only to this site's kiosks. */
        pinKey: z.string(),
        settings: SitesSiteSettings,
        welcomeImage: z.object({ fileId: Uuid, url: z.string() }).nullable(),
        logo: z.object({ fileId: Uuid, url: z.string() }).nullable(),
    }),
    device: z.object({
        id: Uuid,
        name: z.string(),
        printer: Printer.pick({ id: true, name: true, model: true, host: true, port: true, media: true }).nullable(),
        healthReportSeconds: z.number().int(),
    }),
    /** Visible, active groups shown on the kiosk, in kiosk order. */
    groups: z.array(KioskGroup),
    /** The organisation's billing restriction, so a kiosk knows before the first refused event. Absent = none. */
    billing: z.object({ restriction: TenantRestriction, graceUntil: IsoDateTime.nullable() }).optional(),
    /** Extra per-module sections (manifest kioskConfig contributions), keyed by module. */
    sections: z.record(z.string(), z.unknown()),
});
/** Live messages on the `kiosk` channel and the device room (ADR 0007). */
export const KioskLiveMessage = z.discriminatedUnion('type', [
    /** Refetch config (sent after any change that alters the etag). */
    z.object({ type: z.literal('config.changed'), etag: z.string() }),
    /** A visit at this site changed (portal sign-out, another kiosk): update the local open-visit list. */
    z.object({ type: z.literal('visit.opened'), visitId: Uuid, groupId: Uuid, memberId: Uuid.nullable(), displayName: z.string().nullable(), searchName: z.string(), signedInAt: IsoDateTime }),
    z.object({ type: z.literal('visit.closed'), visitId: Uuid }),
    z.object({ type: z.literal('members.changed') }),
    z.object({ type: z.literal('command'), command: DeviceCommand }),
    // Phase 2
    /** A host answered "on my way" / "please wait": show a short banner if the visitor is still at reception. */
    z.object({ type: z.literal('visit.host_reply'), visitId: Uuid, reply: z.enum(['on_my_way', 'please_wait']), hostName: z.string().nullable() }),
    /** The expected list changed (new, changed or cancelled pre-registration): refetch it. */
    z.object({ type: z.literal('expected.changed') }),
]);
// ---------- Settings ----------
export const KioskDevicesTenantSettings = z.object({
    pairingCodeMinutes: z.number().int().min(1).max(1440).default(15),
    healthReportSeconds: z.number().int().min(15).max(3600).default(60),
    alerts: z
        .object({
        /** Email when a kiosk has not reported for this long (0 = off). */
        offlineMinutes: z.number().int().min(0).max(1440).default(10),
        /** Email when battery is below this (0 = off). */
        batteryPercent: z.number().int().min(0).max(100).default(15),
        /** Email when not charging for this long (0 = off). Device monitoring (spec 8.6.14). */
        notChargingMinutes: z.number().int().min(0).max(1440).default(30),
        printerFault: z.boolean().default(true),
        /** Email when the printer reports fewer labels than this (0 = off). */
        labelsLeftBelow: z.number().int().min(0).max(1000).default(20),
        /** Email when the app is older than the platform's latest version. */
        appOutdated: z.boolean().default(true),
        /** Email when events have waited unsent this long while the kiosk reports online (0 = off). Phase 3. */
        queueStuckMinutes: z.number().int().min(0).max(1440).default(30),
    })
        .default({ offlineMinutes: 10, batteryPercent: 15, notChargingMinutes: 30, printerFault: true, labelsLeftBelow: 20, appOutdated: true, queueStuckMinutes: 30 }),
    monitoring: z
        .object({
        /** Days of hourly history kept (0 = no history or uptime). */
        historyDays: z.number().int().min(0).max(30).default(30),
    })
        .default({ historyDays: 30 }),
});
/** Site-level settings of the `kiosk-devices` module (the site's Devices section). */
export const KioskDevicesSiteSettings = z.object({
    /** Fault emails go here. Empty = no fault emails for this site. */
    alertEmail: z.union([z.literal(''), z.email()]).default(''),
    alertsEnabled: z.boolean().default(true),
    /** Phase 3 kiosk behaviour. */
    kiosk: z
        .object({
        /** The kiosk shows an "offline for a while" banner when its config is older than this many hours (0 = never). */
        maxOfflineConfigHours: z.number().int().min(0).max(720).default(72),
    })
        .default({ maxOfflineConfigHours: 72 }),
});
export const KioskDevicesPlatformSettings = z.object({
    /** Newest store build; older apps show "update available" and may raise app_outdated. Empty = don't check. */
    latestAppVersion: z.string().max(40).default(''),
    /** Newest over-the-air screens version. Empty = don't check. */
    latestBundleVersion: z.string().max(40).default(''),
});
// ---------- QR codes ----------
/**
 * QR payloads the kiosk understands (ADR 0008 "QR codes"). Phase 1 prints only badge codes.
 * Badge: `CS1.V.<visit uuid>.<signature>` - signs that visit out (the server checks the signature; badges/qr.ts).
 * Member: `CS1.M.<member uuid>` (Phase 2 onboarding). The id is returned lower-case.
 */
export function parseCloudSignQr(text) {
    const m = /^CS1\.([VM])\.([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\.([A-Za-z0-9_-]{16,64}))?$/i.exec(text.trim());
    if (!m)
        return null;
    const kind = m[1].toUpperCase() === 'V' ? 'visit' : 'member';
    if (kind === 'member' && m[3])
        return null;
    return { kind, id: m[2].toLowerCase() };
}
export function visitQrPayload(visitId) {
    return `CS1.V.${visitId}`;
}
//# sourceMappingURL=kiosk.js.map